import { useCallback, useEffect } from 'react';

import { RuntimeControlFeatures, RuntimeControls } from '@xemplo/runtime-controls';
import { safeCallWithSentryAsync } from '@xemplo/safe-call';
import {
  DEFAULT_TRACKER_DISABLED_CONFIG,
  DEFAULT_TRACKER_ENABLED_CONFIG,
  TrackingFactory,
} from '@xemplo/tracking';
import { Environment, SupportedApps, XemploCISettings } from '@xemplo/types';

/**
 * Initialises Amplitude Analytics and Experiment for the current app.
 *
 * The setup is two-phase: the Amplitude Experiment client must be fetched first
 * (`initAmplitudeFeatureFlag`) so that remote feature flags — including the
 * `AmplitudeConfig` flag — are resolved before the analytics SDK is configured.
 * Only once the Experiment client is ready does `initAmplitude` run.
 *
 * @param settings - App-level environment settings sourced from `XemploAppSettings`.
 */
export const useAmplitudeInit = (settings: XemploCISettings) => {
  /**
   * Configures the Amplitude Analytics SDK using the remote `AmplitudeConfig`
   * feature flag fetched by `RuntimeControls`.
   *
   * Falls back to environment-based defaults when the flag is unavailable
   * (e.g. network failure): tracking enabled on prod, disabled elsewhere.
   * This avoids accidentally silencing prod analytics on a fetch error.
   */
  const initAmplitude = useCallback(() => {
    let trackingConfig = RuntimeControls.getConfig(
      RuntimeControlFeatures.AmplitudeConfig,
    );

    if (!trackingConfig) {
      // API request failed or config not found, defaulting to prod settings for safety
      trackingConfig =
        settings.VITE_RELEASE_CHANNEL === Environment.Prod
          ? DEFAULT_TRACKER_ENABLED_CONFIG
          : DEFAULT_TRACKER_DISABLED_CONFIG;
    }

    TrackingFactory.amplitude(settings.VITE_TRACKING_KEY ?? '', {
      instanceName: settings.VITE_RELEASE_CHANNEL,
      disabled: !!trackingConfig?.disabled,
      options: {
        sessionReplay: trackingConfig?.sessionReplay,
      },
    });
  }, [settings.VITE_RELEASE_CHANNEL, settings.VITE_TRACKING_KEY]);

  /**
   * Initialises the Amplitude Experiment client via `RuntimeControls`, which
   * fetches remote feature flags (including `AmplitudeConfig`) for this deployment.
   *
   * Analytics initialisation (`initAmplitude`) is gated on a successful client
   * response so that the correct remote config is always available before the
   * analytics SDK starts.
   */
  const initAmplitudeFeatureFlag = useCallback(async () => {
    const client = await RuntimeControls.initialise({
      deploymentKey: settings.VITE_TRACKING_KEY ?? '',
      instanceName: settings.VITE_RELEASE_CHANNEL,
      appName: Object.values(SupportedApps).includes(
        settings.VITE_APP_NAME as SupportedApps,
      )
        ? settings.VITE_APP_NAME
        : undefined,
    });

    if (client) initAmplitude();
  }, [
    settings.VITE_TRACKING_KEY,
    settings.VITE_RELEASE_CHANNEL,
    settings.VITE_APP_NAME,
    initAmplitude,
  ]);

  useEffect(() => {
    safeCallWithSentryAsync(initAmplitudeFeatureFlag());
  }, [initAmplitudeFeatureFlag]);
};
